import React, { useState, useEffect, useRef } from 'react';

const ORANGE = '#f97316';
const ORANGE_DARK = '#ea6c0a';
const ORANGE_LIGHT = '#fff7ed';
const ORANGE_GLOW = 'rgba(249,115,22,0.18)';

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'DM Sans', sans-serif; background: #fff; color: #111827; overflow-x: hidden; }
  body.menu-open { overflow: hidden; }

  @keyframes fadeUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes glow-pulse { 0%,100% { box-shadow: 0 0 20px rgba(249,115,22,0.3); } 50% { box-shadow: 0 0 40px rgba(249,115,22,0.6); } }
  @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }

  .fade-up { animation: fadeUp 0.65s cubic-bezier(.16,1,.3,1) both; }
  .float-anim { animation: float 4s ease-in-out infinite; }
  .glow-pulse { animation: glow-pulse 2.5s ease-in-out infinite; }

  .nav-link-hover:hover { color: ${ORANGE} !important; }
  .btn-hover:hover { opacity: .88; transform: translateY(-1px); transition: all .2s; }
  .card-hover:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 48px rgba(249,115,22,0.14), 0 4px 16px rgba(0,0,0,0.06) !important;
    border-color: rgba(249,115,22,0.3) !important;
  }
  .card-hover { transition: all .3s cubic-bezier(.16,1,.3,1); }
  .process-card:hover .process-num-inner { background: ${ORANGE} !important; color: #fff !important; transform: scale(1.08); }
  .process-num-inner { transition: all .25s; }

  .form-input:focus, .form-select:focus, .form-textarea:focus {
    outline: none; border-color: ${ORANGE} !important;
    box-shadow: 0 0 0 3px rgba(249,115,22,0.12) !important;
  }
  .floating-demo:hover { transform: translateY(-3px) scale(1.04); box-shadow: 0 12px 32px rgba(249,115,22,0.5) !important; }
  .floating-demo { transition: all .25s; }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #f9fafb; }
  ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: ${ORANGE}; }

  .reveal { opacity: 0; transform: translateY(24px); transition: opacity .7s ease, transform .7s cubic-bezier(.16,1,.3,1); }
  .reveal.visible { opacity: 1; transform: translateY(0); }

  .gradient-text {
    background: linear-gradient(135deg, ${ORANGE} 0%, #fb923c 60%, #fbbf24 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .tag-pill { transition: all .2s; cursor: default; }
  .tag-pill:hover { background: ${ORANGE} !important; color: #fff !important; }
  .divider { height: 1px; background: linear-gradient(90deg, transparent, #e5e7eb 30%, #e5e7eb 70%, transparent); }
  .nav-scrolled { box-shadow: 0 2px 20px rgba(0,0,0,0.07) !important; }

  .container { width: 100%; max-width: 1280px; margin: 0 auto; }

  @media (max-width: 1024px) {
    .nav-links-desktop { display: none !important; }
    .hero-layout { flex-direction: column !important; text-align: center; }
    .hero-layout > div { max-width: 100% !important; display: flex; flex-direction: column; align-items: center; }
    .hero-section { padding: 40px 40px 60px !important; }
    nav { padding: 0 40px !important; }
    .two-col { flex-direction: column !important; gap: 48px !important; }
  }

  @media (max-width: 768px) {
    .pain-grid { grid-template-columns: 1fr !important; }
    .features-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .benefits-grid { grid-template-columns: 1fr !important; }
    .process-steps { flex-direction: column; align-items: center; }
    .footer-top { flex-direction: column; gap: 40px !important; }
    .hero-title { font-size: 42px !important; }
    .section-title { font-size: 32px !important; }
    .form-row { grid-template-columns: 1fr !important; }
    section { padding: 60px 24px !important; }
    nav { padding: 0 24px !important; }
    footer { padding: 60px 24px 32px !important; }
    .nav-auth-btns { display: none !important; }
  }

  @media (max-width: 480px) {
    .features-grid { grid-template-columns: 1fr !important; }
    .hero-title { font-size: 32px !important; line-height: 1.2 !important; }
    .hero-section { padding: 40px 20px 60px !important; }
    .hero-btns { flex-direction: column; width: 100%; }
    .hero-btns button { width: 100%; }
    .floating-demo { bottom: 20px !important; right: 20px !important; padding: 12px 20px !important; font-size: 13px !important; }
  }
`;

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const SunIcon = ({ size = 20, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

function SVGIcon({ type, size = 18, color = 'currentColor' }) {
  const icons = {
    phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.58 4.2C1.58 3.09 2.46 2 3.56 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />,
    arrow: <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>,
    play: <><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" /></>,
    cal: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    check: <polyline points="20 6 9 17 4 12" />,
    menu: <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></>,
    x: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[type]}
    </svg>
  );
}

function DashboardMockup() {
  const bars1 = [22, 38, 28, 50, 42, 60, 52, 68, 55, 76];
  const bars2 = [42, 62, 38, 72, 58, 48, 82, 68, 75, 60];
  return (
    <div style={{ background: '#0f172a', borderRadius: 14, padding: '20px 10px', fontFamily: "'DM Sans', sans-serif", overflowX: 'auto' }}>
      <div style={{ minWidth: 400 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #1e293b', paddingLeft: 10 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {['#ef4444', '#f59e0b', '#22c55e'].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
          </div>
          <div style={{ flex: 1, background: '#1e293b', borderRadius: 4, height: 18, marginLeft: 8, display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
            <span style={{ fontSize: 9, color: '#475569' }}>Solar ERP — Live Dashboard</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, paddingLeft: 10, paddingRight: 10 }}>
          <div style={{ width: 110, flexShrink: 0 }}>
            <div style={{ background: '#1e293b', borderRadius: 8, padding: '8px 6px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 18, height: 18, borderRadius: 5, background: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><SunIcon size={10} /></div>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#f8fafc' }}>Solar ERP</span>
            </div>
            {['📊 Analytics', '📁 Projects', '🎯 Leads', '📋 Surveys', '💳 Invoices', '⚙️ Services', '👥 Clients'].map((item, i) => (
              <div key={item} style={{
                padding: '5px 8px', borderRadius: 6, marginBottom: 3,
                background: i === 0 ? `linear-gradient(135deg, ${ORANGE}, #fb923c)` : 'transparent',
                color: i === 0 ? '#fff' : '#64748b', fontSize: 10, fontWeight: i === 0 ? 600 : 400,
              }}>{item}</div>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 10 }}>
              {[
                { label: 'Active Projects', val: '77%', color: ORANGE, icon: '📈' },
                { label: 'Total Leads', val: '248', color: '#3b82f6', icon: '🎯' },
                { label: 'Revenue', val: '₹4.8L', color: '#22c55e', icon: '💰' },
              ].map(s => (
                <div key={s.label} style={{ background: '#1e293b', borderRadius: 8, padding: '10px 10px 8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 8, color: '#475569', fontWeight: 500 }}>{s.label}</span>
                    <span style={{ fontSize: 12 }}>{s.icon}</span>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</div>
                  <div style={{ fontSize: 8, color: '#22c55e', marginTop: 4 }}>↑ +12% this month</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              {[
                { title: 'Revenue Trend', note: 'This Year', bars: bars1, colorFn: (i) => i === bars1.length - 1 ? `linear-gradient(to top, ${ORANGE}, #fbbf24)` : i > 6 ? '#1d4ed8aa' : '#1d4ed855' },
                { title: 'Team Output', note: '+18%', bars: bars2, colorFn: (i) => i % 2 === 0 ? `linear-gradient(to top, ${ORANGE}cc, #fb923c88)` : '#f59e0b55' },
              ].map(chart => (
                <div key={chart.title} style={{ background: '#1e293b', borderRadius: 8, padding: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{chart.title}</span>
                    <span style={{ fontSize: 8, color: ORANGE }}>{chart.note}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 44 }}>
                    {chart.bars.map((h, i) => (
                      <div key={i} style={{ flex: 1, borderRadius: '2px 2px 0 0', background: chart.colorFn(i), height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: 8 }}>
              <div style={{ background: '#1e293b', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: `conic-gradient(${ORANGE} 0% 72%, #1e40af 72% 88%, #0f172a 88% 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>72%</span>
                  </div>
                </div>
              </div>
              <div style={{ background: '#1e293b', borderRadius: 8, padding: '8px 10px' }}>
                {[['Conversion Rate', '39.5%', '🟠'], ['Avg Deal Size', '₹2.14L', '🔵'], ['Pipeline', '₹18.4L', '🟢']].map(([k, v, dot]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginBottom: 5, alignItems: 'center' }}>
                    <span style={{ color: '#64748b' }}>{dot} {k}</span>
                    <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FlowDiagram() {
  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '0 20px' }}>
      <style>{`
        @media (max-width: 868px) {
          .process-steps { flex-direction: column; }
          .process-arrow { transform: rotate(90deg) !important; margin: 4px 0; }
        }
      `}</style>
      <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
        {[
          { title: 'Built for Solar Industry', desc: 'Designed specifically for solar businesses — from site surveys and loans to installation and service.', icon: '☀️', color: '#f59e0b' },
          { title: 'Loan & Subsidy Management', desc: 'Track loan applications, approvals, rejections, and subsidy status — all in one system.', icon: '🏦', color: '#22c55e' },
          { title: 'All-in-One Platform', desc: 'No need for multiple tools — manage leads, projects, payments, and reports in one place.', icon: '🧩', color: '#3b82f6' },
          { title: 'Complete Business Visibility', desc: 'Get real-time insights into projects, team performance, and revenue — all from one dashboard.', icon: '📊', color: '#8b5cf6' },
          { title: 'Easy to Use & Scale', desc: 'Simple interface for daily use, powerful enough to scale your solar business operations.', icon: '🚀', color: '#10b981' },
        ].map(item => (
          <div key={item.title} style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 14, padding: '20px 18px', flex: '1 1 160px', maxWidth: 200, textAlign: 'center',
          }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${item.color}22`, border: `1px solid ${item.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, margin: '0 auto 12px' }}>{item.icon}</div>
            <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 13, marginBottom: 6 }}>{item.title}</div>
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{item.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ id, style, children, className = '' }) {
  const ref = useReveal();
  return (
    <section id={id} style={style} className={`reveal ${className}`} ref={ref}>
      {children}
    </section>
  );
}

function SectionHeader({ badge, badgeBg, badgeColor, title, highlight, subtitle, tag = 'gradient' }) {
  return (
    <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
      {badge && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: badgeBg || `${ORANGE}14`, color: badgeColor || ORANGE_DARK, padding: '5px 14px', borderRadius: 100, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
          {badge}
        </div>
      )}
      <h2 className="section-title" style={{ fontSize: 38, fontWeight: 900, color: '#111827', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.02em', marginBottom: 12 }}>
        {title}{highlight && <> <span className="gradient-text">{highlight}</span></>}
      </h2>
      {subtitle && <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.7 }}>{subtitle}</p>}
    </div>
  );
}

export default function App() {
  const [formData, setFormData] = useState({ name: '', company: '', phone: '', email: '', businessType: '', needs: '' });
  const [errors, setErrors] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const f = (key) => (e) => setFormData(p => ({ ...p, [key]: e.target.value }));

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    // Simple validation
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.company.trim()) newErrors.company = 'Company name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to the first error area if needed, though 'contact' is already visible
      scrollTo('contact');
      return;
    }

    setErrors({});
    setSubmitting(true);

    fetch('/api/V1/capture-lead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        full_name: formData.name,
        company: formData.company,
        phone: formData.phone,
        email: formData.email,
        message: formData.needs
      }),
    })
      .then(response => response.json())
      .then(data => {
        setSubmitting(false);
        if (data.status === 'success') {
          setSubmitted(true);
          setFormData({ name: '', company: '', phone: '', email: '', businessType: '', needs: '' });
          // Optional: reset success message after 5 seconds
          // setTimeout(() => setSubmitted(false), 5000);
        } else {
          setErrors({ submit: data.message || 'Something went wrong. Please try again.' });
        }
      })
      .catch(error => {
        setSubmitting(false);
        setErrors({ submit: 'Failed to connect to server. Please try again later.' });
        console.error('Error submitting form:', error);
      });
  };

  return (
    <>
      <style>{globalCSS}</style>
      <div style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* NAVBAR */}
        <nav style={{
          position: 'sticky', top: 0, zIndex: 1000,
          background: scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${scrolled ? '#e5e7eb' : 'transparent'}`,
          padding: '0 80px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: 68, transition: 'all .3s',
        }} className={scrolled ? 'nav-scrolled' : ''}>
          <a href="#hero" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${ORANGE_GLOW}` }}>
              <SunIcon size={18} />
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#111827', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.02em' }}>
              Solar<span style={{ color: ORANGE }}>ERP</span>
            </span>
          </a>

          {/* Hamburger Menu Icon (Mobile Only) */}
          <button
            className="nav-mobile-toggle"
            style={{
              display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: '#111827', padding: 8
            }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <SVGIcon type={isMobileMenuOpen ? 'x' : 'menu'} size={24} />
          </button>

          <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
            <div className="nav-links-desktop" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
              {[['features', 'Features'], ['why-solar-choose', 'Why Choose Us'], ['contact', 'Contact']].map(([id, label]) => (
                <button key={id} className="nav-link-hover" style={{ fontSize: 15, color: '#374151', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', transition: 'color .2s', fontFamily: 'inherit' }} onClick={() => scrollTo(id)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="nav-auth-btns" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <a href="/login" style={{ textDecoration: 'none' }}>
                <button className="btn-hover" style={{ padding: '8px 20px', border: '1.5px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer', color: '#374151', fontFamily: 'inherit' }}>Login</button>
              </a>
              <button className="btn-hover" style={{ padding: '10px 22px', background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => scrollTo('contact')}>
                Book Demo →
              </button>
            </div>
          </div>

          <style>{`
            @media (max-width: 1024px) {
              .nav-mobile-toggle { display: block !important; }
            }
          `}</style>
        </nav>

        {/* MOBILE MENU DRAWER */}
        {isMobileMenuOpen && (
          <div style={{
            position: 'fixed', top: 68, left: 0, width: '100%', height: 'calc(100vh - 68px)',
            background: '#fff', zIndex: 999, padding: '40px 24px',
            display: 'flex', flexDirection: 'column', gap: 24,
            animation: 'slideInRight 0.3s ease-out both'
          }}>
            {[['features', 'Features'], ['why-solar-choose', 'Why Choose Us'], ['contact', 'Contact']].map(([id, label]) => (
              <button key={id} style={{
                textAlign: 'left', fontSize: 20, fontWeight: 600, color: '#111827',
                background: 'none', border: 'none', padding: '12px 0', borderBottom: '1px solid #f3f4f6'
              }} onClick={() => { scrollTo(id); setIsMobileMenuOpen(false); }}>
                {label}
              </button>
            ))}
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 40 }}>
              <a href="/login" style={{ textDecoration: 'none' }}>
                <button style={{ width: '100%', padding: '16px', borderRadius: 12, background: '#f9fafb', color: '#111827', fontWeight: 600, border: '1px solid #e5e7eb' }}>Login</button>
              </a>
              <button style={{ width: '100%', padding: '16px', borderRadius: 12, background: ORANGE, color: '#fff', fontWeight: 700, border: 'none' }} onClick={() => { scrollTo('contact'); setIsMobileMenuOpen(false); }}>
                Book Demo →
              </button>
            </div>
          </div>
        )}

        {/* HERO */}
        <section id="hero" style={{ background: 'linear-gradient(155deg, #fffbf5 0%, #fff7ed 40%, #f0f9ff 100%)', padding: '80px 80px 72px', display: 'flex', alignItems: 'center', gap: 60, minHeight: 'calc(100vh - 68px)', position: 'relative', overflow: 'hidden' }} className="hero-layout hero-section">
          <div style={{ position: 'absolute', top: -100, right: -100, width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${ORANGE}14 0%, transparent 70%)`, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -80, left: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, #3b82f614 0%, transparent 70%)', pointerEvents: 'none' }} />

          <div style={{ flex: 1, maxWidth: 580, position: 'relative', zIndex: 1 }}>
            <div className="fade-up" style={{ animationDelay: '0.05s' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${ORANGE}40`, color: '#92400e', padding: '6px 16px 6px 10px', borderRadius: 100, fontSize: 13, fontWeight: 600, marginBottom: 28, boxShadow: `0 2px 12px ${ORANGE_GLOW}` }}>
                <span style={{ background: `linear-gradient(135deg, ${ORANGE}, #fbbf24)`, borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>⭐</span>
                Built for Solar Businesses Like Yours
              </div>
            </div>
            <h1 className="fade-up hero-title" style={{ fontSize: 54, fontWeight: 900, lineHeight: 1.1, color: '#111827', marginBottom: 22, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.03em', animationDelay: '0.1s' }}>
              Close More Solar Deals{' '}
              <span className="gradient-text">Without the Excel Chaos</span>
            </h1>
            <p className="fade-up" style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.75, marginBottom: 38, animationDelay: '0.18s', maxWidth: 500 }}>
              Track every customer, loan, and installation - all in one powerful platform built exclusively for solar businesses.
            </p>
            <div className="fade-up" style={{ display: 'flex', gap: 14, alignItems: 'center', animationDelay: '0.26s' }}>
              <button className="btn-hover" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 28px', background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: `0 8px 24px ${ORANGE_GLOW}` }} onClick={() => scrollTo('contact')}>
                <SVGIcon type="phone" size={16} color="#fff" /> Book Free Demo
              </button>
              <a href="/login" style={{ textDecoration: 'none' }}>
                <button className="btn-hover" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 24px', background: '#fff', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Login
                </button>
              </a>
            </div>
            <div className="fade-up" style={{ display: 'flex', gap: 32, marginTop: 44, animationDelay: '0.34s', paddingTop: 32, borderTop: '1px solid #f3f4f6' }}>
              {[['50+', 'Projects Managed'], ['40%', 'Improved Team Efficiency'], ['3–5h', 'Time Saved Per Day']].map(([num, label]) => (
                <div key={label}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#111827', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{num}</div>
                  <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="fade-up" style={{ flex: 1, position: 'relative', zIndex: 1, animationDelay: '0.15s', animation: 'fadeUp 0.65s 0.15s both, float 4s 0.8s ease-in-out infinite' }}>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: -14, right: 20, background: '#10b981', color: '#fff', padding: '6px 16px', borderRadius: 100, fontSize: 12, fontWeight: 700, boxShadow: '0 4px 14px rgba(16,185,129,0.4)', display: 'flex', alignItems: 'center', gap: 6, zIndex: 10 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />Live Dashboard
              </div>
              <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 32px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #e5e7eb', padding: 4 }}>
                <div style={{ borderRadius: 16, overflow: 'hidden' }}><DashboardMockup /></div>
              </div>
              <div style={{ position: 'absolute', bottom: -16, left: -16, background: '#fff', borderRadius: 14, padding: '10px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #f3f4f6' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>⚡</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>New Lead Captured</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Just now · Rooftop Solar 8kW</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PAIN POINTS */}
        <Section style={{ background: '#f9fafb', padding: '88px 80px' }}>
          <SectionHeader badge="⚠ Common Pain Points" badgeBg="#fef2f2" badgeColor="#ef4444"
            title="Running a Solar Business" highlight="Shouldn't Feel This Chaotic"
            subtitle="If you're managing projects on Excel, calls, and WhatsApp - this probably feels familiar." />
          <div className="pain-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, maxWidth: 1080, margin: '0 auto' }}>
            {[
              { icon: '📉', color: '#ef4444', title: 'Leads slipping through follow-ups', desc: 'Leads slipping through	 calls, WhatsApp, and missed follow-ups.' },
              { icon: '📊', color: '#f59e0b', title: 'Excel confusion', desc: 'Managing projects in Excel with confusion and errors.' },
              { icon: '👁️', color: '#8b5cf6', title: 'No project visibility', desc: 'No clear visibility of project status.' },
              { icon: '💸', color: '#ef4444', title: 'Payment chaos', desc: 'Payment tracking confusion.' },
              { icon: '🤝', color: '#3b82f6', title: 'Poor Team Coordination', desc: 'Sales and operations teams working in silos with costly miscommunication.' },
              { icon: '🏦', color: '#0ea5e9', title: 'Loan tracking chaos', desc: 'No proper tracking of loan applications, approvals, and rejections across banks' }
            ].map((p, i) => (
              <div key={i} className="card-hover" style={{ background: '#fff', borderRadius: 16, padding: '28px 24px', border: '1.5px solid #fee2e2', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <div style={{ width: 56, height: 56, borderRadius: 16, background: `${p.color}14`, border: `1.5px solid ${p.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>{p.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.title}</h3>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.65 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        <div className="divider" />

        {/* ONE PLATFORM */}
        <Section style={{ background: '#fff', padding: '88px 80px' }}>
          <SectionHeader badge="🌟 All-In-One Platform" title="One Platform to Run Your" highlight="Entire Solar Business"
            subtitle="From lead capture to loan, installation, and payment - everything runs in one connected system." />
          <div className="two-col" style={{ display: 'flex', gap: 64, alignItems: 'center', maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ flex: 1 }}>
              {[
                { icon: '🎯', color: ORANGE, title: 'Centralized Lead Management', desc: 'Capture every inquiry from multiple channels and track them through your sales pipeline without losing a single opportunity.' },
                { icon: '💰', color: '#22c55e', title: 'Loan & Subsidy Management', desc: 'Track loan applications, approvals, rejections, and subsidy — all in one place.' },
                { icon: '⚡', color: '#3b82f6', title: 'Automated Workflows', desc: 'Automate your process from lead → loan → installation — reduce manual work and save hours daily.' },
              ].map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 28, alignItems: 'flex-start' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0, background: `${f.color}14`, border: `1.5px solid ${f.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{f.icon}</div>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.title}</h3>
                    <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.65 }}>{f.desc}</p>
                  </div>
                </div>
              ))}
              <div style={{ background: 'linear-gradient(135deg, #fffbeb, #fff7ed)', border: `1px solid ${ORANGE}40`, borderRadius: 14, padding: '18px 20px', marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 20 }}>⭐</span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Built Specifically for Solar</div>
                    <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>Built specifically for solar businesses — from site surveys and loans to installation and subsidy tracking.</p>
                  </div>
                </div>
              </div>
              <button className="btn-hover" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 28, padding: '14px 28px', background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: `0 6px 20px ${ORANGE_GLOW}` }} onClick={() => scrollTo('contact')}>
                See How It Works <SVGIcon type="arrow" size={16} color="#fff" />
              </button>
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              <div style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #ffe4c4 100%)', borderRadius: 24, padding: 28, position: 'relative' }}>
                <div style={{ background: '#1e293b', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 40px rgba(0,0,0,0.3)' }}>
                  <img src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&q=80" alt="Solar installation" style={{ width: '100%', height: 200, objectFit: 'cover', opacity: 0.75 }} />
                  <div style={{ padding: '16px 16px 12px' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {['Lead Mgmt', 'Site Survey', 'Installation', 'Invoicing', 'Analytics'].map(t => (
                        <div key={t} className="tag-pill" style={{ background: '#334155', borderRadius: 6, padding: '4px 12px', fontSize: 11, color: '#94a3b8', fontWeight: 500, border: '1px solid #475569' }}>{t}</div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ position: 'absolute', bottom: 20, right: 20, background: '#10b981', color: '#fff', padding: '8px 18px', borderRadius: 100, fontSize: 12, fontWeight: 700, boxShadow: '0 4px 14px rgba(16,185,129,0.4)' }}>✓ All-in-One Solution</div>
              </div>
            </div>
          </div>
        </Section>

        {/* FEATURES GRID */}
        <Section id="features" style={{ background: '#fffbf5', padding: '88px 80px' }}>
          <SectionHeader badge="🛠 Feature Suite" title="Everything You Need —" highlight="Built for Solar"
            subtitle="From lead management to installation, loan tracking, and payments — everything your team needs is connected." />
          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, maxWidth: 1200, margin: '0 auto 64px' }}>
            {[
              { icon: '🎯', color: ORANGE, title: 'Lead Management', desc: 'Capture and track every inquiry from first contact to conversion.' },
              { icon: '🏦', color: '#3b82f6', title: 'Loan & Subsidy Management', desc: 'Track loan applications, approvals, rejections, and subsidy status across banks — all in one place.' },
              { icon: '🔆', color: '#22c55e', title: 'Installation Tracking', desc: 'Track installation progress, assign teams, and monitor status in real-time.' },
              { icon: '💳', color: '#8b5cf6', title: 'Invoicing & Payments', desc: 'Generate invoices, track payments, and manage cash/loan transactions easily.' },
              { icon: '👤', color: '#f59e0b', title: 'Customer Management', desc: 'Manage complete customer details, documents, and project history in one place.' },
              { icon: '🏠', color: '#10b981', title: 'Site Survey Management', desc: 'Schedule site visits, collect data, and generate survey reports easily.' },
              { icon: '🔋', color: '#6366f1', title: 'Inventory Management', desc: 'Track solar panels, inverters, and all materials.' },
              { icon: '👷', color: '#ec4899', title: 'Team & Task Management', desc: 'Assign work and track team productivity in real time.' },
            ].map((f, i) => (
              <div key={i} className="card-hover" style={{ background: '#fff', borderRadius: 18, padding: '26px 22px', border: '1.5px solid #f3f4f6', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `${f.color}14`, border: `1.5px solid ${f.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, fontSize: 24 }}>{f.icon}</div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.title}</h3>
                <p style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ maxWidth: 920, margin: '0 auto' }}>
            <div style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #ffe4c4 100%)', borderRadius: 24, padding: 28, boxShadow: '0 8px 40px rgba(249,115,22,0.1)' }}>
              <div style={{ background: '#1e293b', borderRadius: 16, overflow: 'hidden' }}><DashboardMockup /></div>
            </div>
          </div>
        </Section>

        {/* BENEFITS */}
        <Section id="benefits" style={{ background: '#f0f9ff', padding: '88px 80px' }}>
          <SectionHeader badge="📈 Business Impact" badgeBg="#e0f2fe" badgeColor="#0369a1"
            title="Grow Faster with" highlight="Better Control"
            subtitle="Transform your solar business operations and achieve measurable growth" />
          <div className="benefits-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, maxWidth: 1100, margin: '0 auto', alignItems: 'center' }}>
            <div>
              {[
                { icon: '📈', color: '#22c55e', title: 'Increase Lead Conversion', desc: 'Never lose a potential customer. Our systematic lead management increases conversion rates significantly.' },
                { icon: '⏱️', color: '#3b82f6', title: 'Save 3–5 Hours of Manual Work Daily', desc: 'Automate repetitive tasks and eliminate spreadsheet chaos with intelligent workflow automation.' },
                { icon: '🎯', color: ORANGE, title: 'Never Miss a Project Deadline', desc: 'Real-time tracking and automated reminders ensure timely completion of every solar installation.' },
                { icon: '👥', color: '#8b5cf6', title: 'Improve Team Coordination', desc: 'Keep sales, operations, and service teams aligned with centralized communication and task management.' },
                { icon: '📊', color: '#06b6d4', title: 'Real-Time Business Insights', desc: 'Make confident decisions with comprehensive analytics and reporting across your entire solar business.' },
              ].map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: `${b.color}14`, border: `1.5px solid ${b.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{b.icon}</div>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{b.title}</h3>
                    <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', borderRadius: 22, padding: 28, boxShadow: '0 8px 32px rgba(16,185,129,0.1)' }}>
                <div style={{ background: '#1e293b', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 40px rgba(0,0,0,0.3)' }}>
                  <div style={{ background: '#0f172a', padding: 20 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                      {['Solar Company', 'Annual View', '2024'].map(t => (
                        <div key={t} style={{ background: '#1e293b', border: '1px solid #334155', padding: '4px 12px', borderRadius: 6, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{t}</div>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                      {[['Revenue', '₹4.83Cr', ORANGE, '+8.75%'], ['Projects', '383', '#3b82f6', '+12%']].map(([k, v, c, ch]) => (
                        <div key={k} style={{ background: '#1e293b', borderRadius: 10, padding: 14, border: `1px solid ${c}22` }}>
                          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6 }}>{k}</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</div>
                          <div style={{ fontSize: 10, color: '#22c55e', marginTop: 6, fontWeight: 600 }}>↑ {ch} this year</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: '#1e293b', borderRadius: 10, padding: 14, border: '1px solid #334155' }}>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 10, fontWeight: 600 }}>Monthly Revenue</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 56 }}>
                        {[30, 50, 40, 65, 55, 70, 60, 80, 72, 90, 84, 96].map((h, i) => (
                          <div key={i} style={{ flex: 1, borderRadius: '3px 3px 0 0', background: i === 11 ? `linear-gradient(to top, ${ORANGE}, #fbbf24)` : i > 8 ? '#3b82f6aa' : '#3b82f655', height: `${h}%` }} />
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                        {['Jan', 'Mar', 'May', 'Jul', 'Sep', 'Nov'].map(m => <span key={m} style={{ fontSize: 8, color: '#334155' }}>{m}</span>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* PROCESS */}
        <Section style={{ background: '#fff', padding: '88px 80px' }}>
          <SectionHeader badge="🔄 5-Step Workflow" title="Simple Process." highlight="Powerful Results"
            subtitle="Streamline your solar business with our intuitive 5-step workflow" />
          <div className="process-steps" style={{ display: 'flex', gap: 20, justifyContent: 'center', maxWidth: 1200, margin: '0 auto', flexWrap: 'wrap' }}>
            {[
              { num: 1, color: ORANGE, icon: '📞', title: 'Capture Lead', desc: 'Capture inquiries from calls, WhatsApp, and website — all in one place.' },
              { num: 2, color: '#f59e0b', icon: '📝', title: 'Create Quotation', desc: 'Generate and manage quotations with proper approval and pricing control.' },
              { num: 3, color: '#3b82f6', icon: '🏦', title: 'Process Loan & Subsidy', desc: 'Apply, track, and manage loan approvals, rejections, and subsidy status across banks.' },
              { num: 4, color: '#22c55e', icon: '🔆', title: 'Manage Installation', desc: 'Assign installers, schedule work, and track real-time installation progress.' },
              { num: 5, color: '#8b5cf6', icon: '💳', title: 'Invoice & Complete', desc: 'Generate invoices, track payments, and mark project completion with full visibility.' },
            ].map((step, i) => (
              <div key={i} className="process-card" style={{ flex: '1 1 180px', maxWidth: 210, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative' }}>
                <div className="process-num-inner" style={{ width: 60, height: 60, borderRadius: '50%', background: `${step.color}18`, border: `2.5px solid ${step.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, color: step.color, marginBottom: 20, position: 'relative', zIndex: 1 }}>{step.num}</div>
                <div style={{ background: '#fff', borderRadius: 18, padding: '24px 18px', border: '1.5px solid #f3f4f6', width: '100%', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{step.icon}</div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{step.title}</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* FLOW DIAGRAM */}
        <Section id="why-solar-choose" style={{ background: '#0f172a', padding: '88px 80px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
            <h2 style={{ fontSize: 42, fontWeight: 900, color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.03em', marginBottom: 12 }}>
              <span className="gradient-text">Why Solar Businesses Choose</span> This ERP
            </h2>
            <p style={{ fontSize: 16, color: '#64748b', lineHeight: 1.7 }}>Built specifically for solar companies to manage real-world operations — not just generic CRM tasks.</p>
          </div>
          <FlowDiagram />
        </Section>

        {/* CTA */}
        <Section style={{ background: 'linear-gradient(155deg, #fffbf5 0%, #fff7ed 60%, #f0f9ff 100%)', padding: '88px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -120, left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${ORANGE}0c 0%, transparent 70%)`, pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${ORANGE}50`, color: '#ea580c', padding: '6px 18px', borderRadius: 100, fontSize: 13, fontWeight: 600, marginBottom: 24, boxShadow: `0 2px 12px ${ORANGE_GLOW}` }}>
              <span style={{ color: ORANGE }}>🔥</span> Free demo slots available this week.
            </div>
            <h2 style={{ fontSize: 46, fontWeight: 900, color: '#111827', marginBottom: 16, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.03em' }}>
              Take Full Control of Your Solar Business — {' '}<span className="gradient-text">Start Today</span>
            </h2>
            <p style={{ fontSize: 17, color: '#6b7280', maxWidth: 560, margin: '0 auto 40px', lineHeight: 1.75 }}>
              See how you can manage leads, loans, installations, and payments — all in one system.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 56, flexWrap: 'wrap' }}>
              <button className="btn-hover" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 34px', background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: `0 10px 30px ${ORANGE_GLOW}` }} onClick={() => scrollTo('contact')}>
                <SVGIcon type="cal" size={18} color="#fff" /> Book Free Demo
              </button>
              <a href="/login" style={{ textDecoration: 'none' }}>
                <button className="btn-hover" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 28px', background: '#fff', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Login
                </button>
              </a>
            </div>
            <div style={{ display: 'flex', gap: 28, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[['⚡', 'Quick Setup', 'Get started in under 30 minutes'], ['🔄', '24/7 Support', 'Dedicated support team ready to help'], ['✓', 'Proven Results', 'Built for real solar business']].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 200 }}>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, marginBottom: 14, boxShadow: `0 6px 20px ${ORANGE_GLOW}` }}>{icon}</div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
                  <p style={{ fontSize: 14, color: '#6b7280' }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* DEMO FORM */}
        <Section id="contact" style={{ background: '#fffbf5', padding: '88px 80px' }}>
          <div style={{ background: '#fff', borderRadius: 24, padding: '52px 56px', boxShadow: '0 8px 48px rgba(0,0,0,0.07)', maxWidth: 760, margin: '0 auto', border: '1.5px solid #f3f4f6', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 200, height: 200, background: `radial-gradient(circle at top right, ${ORANGE}0c, transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: `0 6px 20px ${ORANGE_GLOW}` }}>
                <SunIcon size={26} />
              </div>
              <h2 style={{ fontSize: 30, fontWeight: 900, color: '#111827', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.02em', marginBottom: 8 }}>Get Your Free Demo</h2>
              <p style={{ fontSize: 15, color: '#6b7280' }}>We'll show you exactly how Solar ERP fits your business</p>
            </div>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f0fdf4', borderRadius: 16, border: '1.5px solid #bbf7d0' }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: '#166534', marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Demo Request Submitted!</h3>
                <p style={{ color: '#15803d', fontSize: 15 }}>We'll contact you within 24 hours to schedule your personalized demo.</p>
              </div>
            ) : (
              <>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 0 }}>
                  {[['Full Name', 'name', 'text', 'Enter your full name'], ['Company Name', 'company', 'text', 'Your company name']].map(([lbl, key, type, ph]) => (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                      <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{lbl} *</label>
                      <input className="form-input" type={type} placeholder={ph} value={formData[key]} onChange={(e) => { f(key)(e); if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; }); }} style={{ padding: '12px 16px', border: errors[key] ? '1.5px solid #ef4444' : '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, color: '#111827', fontFamily: 'inherit', background: errors[key] ? '#fff1f1' : '#fafafa', transition: 'all .2s' }} />
                      {errors[key] && <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 500 }}>{errors[key]}</span>}
                    </div>
                  ))}
                </div>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {[['Phone Number', 'phone', 'tel', '+91 00000 00000'], ['Email Address', 'email', 'email', 'you@company.com']].map(([lbl, key, type, ph]) => (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                      <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{lbl} *</label>
                      <input className="form-input" type={type} placeholder={ph} value={formData[key]} onChange={(e) => { f(key)(e); if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; }); }} style={{ padding: '12px 16px', border: errors[key] ? '1.5px solid #ef4444' : '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, color: '#111827', fontFamily: 'inherit', background: errors[key] ? '#fff1f1' : '#fafafa', transition: 'all .2s' }} />
                      {errors[key] && <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 500 }}>{errors[key]}</span>}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 28 }}>
                  <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Tell us about your needs</label>
                  <textarea className="form-textarea" placeholder="What challenges are you facing with your current process?" value={formData.needs} onChange={f('needs')} style={{ padding: '12px 16px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, color: '#111827', fontFamily: 'inherit', background: '#fafafa', resize: 'vertical', minHeight: 110, transition: 'all .2s' }} />
                </div>
                <button className="btn-hover" onClick={handleSubmit} disabled={submitting} style={{ width: '100%', padding: '16px', background: submitting ? '#9ca3af' : `linear-gradient(135deg, ${ORANGE}, #fb923c)`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: submitting ? 'none' : `0 8px 24px ${ORANGE_GLOW}` }}>
                  {submitting ? 'Sending Request...' : <><SunIcon size={18} /> Request My Free Demo</>}
                </button>
                {errors.submit && <p style={{ textAlign: 'center', color: '#ef4444', fontSize: 14, marginTop: 12, fontWeight: 500 }}>{errors.submit}</p>}
                <p style={{ textAlign: 'center', fontSize: 13, color: '#9ca3af', marginTop: 14 }}>🔒 We'll contact you within 24 hours · No credit card required</p>
              </>
            )}
          </div>
        </Section>

        {/* FOOTER */}
        <footer style={{ background: '#0f172a', padding: '64px 80px 40px' }}>
          <div className="footer-top" style={{ display: 'flex', gap: 80, marginBottom: 48 }}>
            <div style={{ flex: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><SunIcon size={16} /></div>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Solar<span style={{ color: ORANGE }}>ERP</span></span>
              </div>
              <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.75, maxWidth: 320 }}>All-in-one ERP platform built specifically for solar businesses. Manage leads, projects, and operations with ease.</p>
              <div style={{ marginTop: 24 }}>
                <a href="mailto:info@blisstech.in" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#94a3b8', fontSize: 14, textDecoration: 'none', transition: 'color .2s' }} className="nav-link-hover">
                  <span>📧</span> info@blisstech.in
                </a>
              </div>
            </div>
            {[
              { title: 'Product', links: [['Features', 'features'], ['Why Choose Us', 'why-solar-choose'], ['Book Demo', 'contact'], ['Login', '/login']] },
              { title: 'Company', links: [['Privacy Policy', 'hero'], ['Contact', 'contact']] },
            ].map(col => (
              <div key={col.title} style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 20, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{col.title}</div>
                {col.links.map(([label, target]) => (
                  <a
                    key={label}
                    href={target.startsWith('/') ? target : `#${target}`}
                    onClick={!target.startsWith('/') ? (e) => { e.preventDefault(); scrollTo(target); } : undefined}
                    className="nav-link-hover"
                    style={{ display: 'block', fontSize: 14, color: '#64748b', marginBottom: 12, textDecoration: 'none', transition: 'color .2s' }}
                  >
                    {label}
                  </a>
                ))}
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 13, color: '#475569' }}>
              © 2026 <a href="https://blisstech.in/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none', transition: 'color .2s' }} className="nav-link-hover">BlissTech</a>. All rights reserved.
            </span>
          </div>
        </footer>

        {/* FLOATING BUTTON */}
        <button className="floating-demo" onClick={() => scrollTo('contact')} style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 999, padding: '14px 24px', background: `linear-gradient(135deg, ${ORANGE}, #fb923c)`, color: '#fff', border: 'none', borderRadius: 100, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: `0 8px 28px ${ORANGE_GLOW}`, display: 'flex', alignItems: 'center', gap: 8, position: 'fixed' }}>
          <SVGIcon type="cal" size={16} color="#fff" /> Book Demo
          <span style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
        </button>

      </div>
    </>
  );
}
